import type { WorkspaceTabDescriptor } from "@/type/workspaceTabs";

/**
 * Which routes may occupy a workspace tab.
 *
 * A central registry rather than `definePage({ meta })`: only 31 of 158 pages call
 * `definePage` at all, so per-page meta would mean editing ~25 live detail pages to
 * ship this. A registry also keeps "what is tabbable, and what is excluded and why"
 * auditable in one place, and is reversible by deleting one file.
 */

export const WORKSPACE_TAB_MAX = 8;

/** Record types that open as tabs, with the label/icon used on the strip. */
export const WORKSPACE_TAB_DESCRIPTORS: WorkspaceTabDescriptor[] = [
  {
    routeName: "healthcare-visits-id",
    icon: "tabler-stethoscope",
    labelKey: "alkokh.workspaceTabs.records.visit",
  },
  {
    routeName: "accounting-sales-invoices-id",
    icon: "tabler-file-invoice",
    labelKey: "alkokh.workspaceTabs.records.salesInvoice",
  },
  {
    routeName: "healthcare-boarding-id",
    icon: "tabler-home-heart",
    labelKey: "alkokh.workspaceTabs.records.boarding",
  },
  {
    routeName: "customers-details-id",
    icon: "tabler-user",
    labelKey: "alkokh.workspaceTabs.records.customer",
  },
  {
    routeName: "guardians-details-id",
    icon: "tabler-users",
    labelKey: "alkokh.workspaceTabs.records.guardian",
  },
  {
    routeName: "pet-profile-id-tab",
    icon: "tabler-paw",
    labelKey: "alkokh.workspaceTabs.records.pet",
  },
];

const descriptorByRoute = new Map(
  WORKSPACE_TAB_DESCRIPTORS.map(item => [item.routeName, item]),
);

/**
 * Routes that must NEVER be cached, because they keep page state in MODULE scope —
 * two live instances would corrupt each other:
 *   `useRatingsAnalytics.ts:75-83`  loading / enriching / loaded / windowed / errored
 *
 * The denylist is checked BEFORE the allowlist so an accidental allowlist entry
 * cannot resurrect that bug.
 */
export const WORKSPACE_TAB_DENYLIST: string[] = [
  "healthcare-ratings",
  "report-engine",
  "report-engine-tab",
];

const denylist = new Set(WORKSPACE_TAB_DENYLIST);

export function isWorkspaceTabRoute(routeName: unknown): boolean {
  const name = typeof routeName === "string" ? routeName : "";
  if (!name || denylist.has(name))
    return false;

  return descriptorByRoute.has(name);
}

export function workspaceTabDescriptor(routeName: unknown): WorkspaceTabDescriptor | null {
  const name = typeof routeName === "string" ? routeName : "";

  return descriptorByRoute.get(name) ?? null;
}
